import { EmbeddedMessageMedium, MessageMedium } from 'constants/messageMediums';
import { CampaignTypeFilter } from 'modules/domains/campaign';
import { DataWithGraphQLTypeName, GRAPHQL_TYPENAMES } from 'modules/graphql/types';
import { MetricType } from './components/constants';
import { AlertStatus, ComparisonOperator, MetricName } from './constants';

/** Alert metrics */

export const ALL_MESSAGE_MEDIUMS = { ...MessageMedium, ...EmbeddedMessageMedium };
export type AllMessageMediums = MessageMedium | EmbeddedMessageMedium;

export type AlertMetric = {
  metricName: MetricName;
  valueType: MetricType;
  mediums: AllMessageMediums[];
};

export type GraphQLAlertMetric = DataWithGraphQLTypeName<
  GRAPHQL_TYPENAMES.ANALYTICS_METRIC,
  AlertMetric
>;

export type AlertMetricsByMedium = Record<AllMessageMediums, GraphQLAlertMetric[]>;

/** Project campaign alerts (saved) */

export type ThresholdInputValue = `${number}`;
// Technically, only integers 1-72 should be allowed for now...
export type THours = `${number}`;

// To learn more about this format: https://en.wikipedia.org/wiki/ISO_8601#Durations
export type TimeDuration = `PT${THours}H`; // eg: "PT8H"

export type CampaignAlert = {
  id: number;
  status: AlertStatus;
  campaignType: CampaignTypeFilter;
  messageMedium: AllMessageMediums;
  metricName: MetricName;
  comparisonOperator: ComparisonOperator;
  threshold: number;
  frequency: TimeDuration;
};

export type SavedAlertsByMedium = Record<AllMessageMediums, CampaignAlert[]>;

// Alert destination

export type CampaignAlertsWebhook = {
  name: string;
  endpoint: string;
  authType: WebhookAuthType;
  authToken?: string;
  clientId?: string;
  clientSecret?: string;
  tokenEndpoint?: string;
};

/** Alerts form */

export type AlertFormattedForForm = {
  status: boolean;
  numberThreshold?: ThresholdInputValue;
  percentageThreshold?: ThresholdInputValue;
  frequency: THours;
};

export type AlertFormattedForSave = {
  id?: CampaignAlert['id'];
  status: CampaignAlert['status'];
  campaignType: CampaignAlert['campaignType'];
  messageMedium: CampaignAlert['messageMedium'];
  metricName: CampaignAlert['metricName'];
  comparisonOperator: CampaignAlert['comparisonOperator'];
  threshold: CampaignAlert['threshold'];
  frequency: CampaignAlert['frequency'];
};

export type AlertsFormState = Record<string, AlertFormattedForForm>;

export type ParsedKey = [AllMessageMediums, CampaignTypeFilter, MetricName];

export type DirtyFields = {
  status?: boolean;
  percentageThreshold?: boolean;
  numberThreshold?: boolean;
  frequency?: boolean;
};

/** Webhooks */

export enum WebhookAuthType {
  BASIC = 'Basic',
  OAUTH2 = 'OAuth2',
  NONE = 'NoAuth',
}

export type AlertsWebhookListItem = {
  id: number;
  name: string;
  endpoint: string;
  enabled: boolean;
};

export type EditOrDeleteWebhookModalProps = { webhookId?: number };

export enum WebhookFormField {
  Name = 'name',
  Endpoint = 'endpoint',
  AuthType = 'authType',

  // Basic
  AuthToken = 'authToken',

  // OAuth 2.0
  ClientId = 'clientId',
  ClientSecret = 'clientSecret',
  TokenEndpoint = 'tokenEndpoint',
}
